import { clampChroma, converter, formatHex, parse, wcagContrast } from 'culori';

/**
 * Organization theme generator.
 *
 * Input: up to three brand colours chosen by the customer (any hex).
 * Output: CSS custom properties for light and dark mode whose text/background
 * pairs meet WCAG AA *by construction* - the generator adjusts lightness
 * (never hue) until required contrast holds, and reports what it changed so
 * the UI can tell the customer.
 *
 * Brand colour is confined to emphasis tokens (primary actions, active
 * navigation, focus ring, links, selected tints). Neutrals and status colours
 * are platform-owned and are never re-tinted.
 */

export interface BrandInput {
  primary: string;
  secondary: string;
  accent: string;
}

export const DEFAULT_BRAND: BrandInput = {
  primary: '#1f5a99',
  secondary: '#12324f',
  accent: '#0b7f72',
};

export interface ThemeAdjustment {
  role: 'primary' | 'secondary' | 'accent';
  mode: 'light' | 'dark';
  reason: string;
  from: string;
  to: string;
}

export interface GeneratedTheme {
  light: Record<string, string>;
  dark: Record<string, string>;
  adjustments: ThemeAdjustment[];
  /** Colours actually used (input after sanitising invalid values back to defaults). */
  input: BrandInput;
}

// Surfaces the derived colours must work on. Kept in sync with tokens.css
// (a unit test asserts this).
export const SURFACES = {
  light: { surface: '#ffffff', bg: '#f5f6f8' },
  dark: { surface: '#141920', bg: '#0c0f14' },
} as const;

export const INK = '#0f1720';
export const WHITE = '#ffffff';

const toOklch = converter('oklch');

interface Lch {
  l: number;
  c: number;
  h: number;
}

const contrast = (a: string, b: string): number => wcagContrast(a, b);

function hex({ l, c, h }: Lch): string {
  const clamped = clampChroma({ mode: 'oklch', l: Math.min(Math.max(l, 0), 1), c: Math.max(c, 0), h }, 'oklch');
  return formatHex(clamped) ?? '#000000';
}

function readColor(input: string): Lch | null {
  const parsed = parse(input);
  if (!parsed) return null;
  const o = toOklch(parsed);
  return { l: o.l, c: o.c ?? 0, h: o.h ?? 0 };
}

export function isValidHex(value: string): boolean {
  return /^#([0-9a-f]{6}|[0-9a-f]{3})$/i.test(value.trim());
}

/** Step lightness toward a goal until `ok(hex)` is true (or we hit the bound). */
function stepUntil(start: Lch, direction: 1 | -1, ok: (hexColor: string) => boolean, step = 0.005): Lch {
  let current = { ...start };
  for (let i = 0; i < 400; i++) {
    if (ok(hex(current))) return current;
    const next = current.l + direction * step;
    if (next < 0 || next > 1) break;
    current = { ...current, l: next };
  }
  return current;
}

interface Family {
  fill: string;
  hover: string;
  pressed: string;
  on: string;
  text: string;
  /** Keyboard-focus ring colour. Usually the brand text colour; neutral for red-ish brands (see focusRingFor). */
  focus: string;
  subtle: string;
  subtleHover: string;
  border: string;
}

/**
 * Focus rings must never be mistaken for validation errors. If the brand hue sits in the red/danger
 * range (school maroons and crimsons), the ring falls back to a neutral high-contrast tone instead
 * of the brand colour; validation errors always carry an icon and message as well.
 */
function focusRingFor(input: Lch, mode: 'light' | 'dark', brandText: string): string {
  const redish = input.c > 0.05 && (input.h <= 45 || input.h >= 345);
  if (!redish) return brandText;
  return mode === 'light' ? '#1d2939' : '#f2f4f7';
}

function deriveFamily(
  input: Lch,
  mode: 'light' | 'dark',
  role: ThemeAdjustment['role'],
  adjustments: ThemeAdjustment[],
  original: string,
): Family {
  const { surface, bg } = SURFACES[mode];
  const chromaCap = Math.min(input.c, 0.2);
  const base: Lch = { l: input.l, c: chromaCap, h: input.h };

  let fill: Lch;
  let on: string;

  if (mode === 'light') {
    if (contrast(WHITE, hex(base)) >= 4.5) {
      fill = base;
      on = WHITE;
    } else if (base.l >= 0.65 && contrast(INK, hex(base)) >= 4.5) {
      // Genuinely light brand colour (yellow, gold, light orange): keep it, use dark text on it.
      fill = base;
      on = INK;
    } else {
      fill = stepUntil(base, -1, (c) => contrast(WHITE, c) >= 4.5);
      on = WHITE;
    }
  } else {
    // Dark mode: primary buttons are light fills with dark text.
    const start: Lch = { ...base, l: Math.max(base.l, 0.66) };
    fill = stepUntil(start, 1, (c) => contrast(INK, c) >= 7);
    on = INK;
  }

  const fillHex = hex(fill);
  if (mode === 'light' && Math.abs(fill.l - input.l) > 0.02) {
    adjustments.push({
      role,
      mode,
      from: original,
      to: fillHex,
      reason: 'Adjusted so button text is readable on this colour (WCAG AA 4.5:1).',
    });
  }

  // Hover/pressed normally move away from the surface (darker on light fills). If that would push the
  // text on the fill below 4.5:1 (dark text on a light brand colour), move the other way instead.
  const direction = on === WHITE ? -1 : mode === 'dark' ? 1 : -1;
  const variant = (delta: number) => {
    const preferred = hex({ ...fill, l: fill.l + direction * delta });
    return contrast(on, preferred) >= 4.5 ? preferred : hex({ ...fill, l: fill.l - direction * delta });
  };
  const hover = variant(0.05);
  const pressed = variant(0.09);

  // Subtle tint (selected rows, active nav background, info banners).
  const subtleLch: Lch =
    mode === 'light'
      ? { l: 0.965, c: Math.min(fill.c * 0.28, 0.035), h: fill.h }
      : { l: 0.27, c: Math.min(fill.c * 0.5, 0.06), h: fill.h };
  const subtleHoverLch: Lch = { ...subtleLch, l: mode === 'light' ? 0.94 : 0.31 };
  const subtle = hex(subtleLch);
  const subtleHover = hex(subtleHoverLch);
  const border = hex(
    mode === 'light'
      ? { l: 0.86, c: Math.min(fill.c * 0.5, 0.07), h: fill.h }
      : { l: 0.42, c: Math.min(fill.c * 0.6, 0.08), h: fill.h },
  );

  // Brand colour used as text / link / icon: must read on surface, page background and its own tint.
  const textStart: Lch = mode === 'light' ? { ...fill, l: Math.min(fill.l, input.l) } : { ...fill, l: Math.max(fill.l, 0.72) };
  const textOk = (c: string) =>
    contrast(c, surface) >= 4.5 &&
    contrast(c, bg) >= 4.5 &&
    contrast(c, subtle) >= 4.5 &&
    contrast(c, subtleHover) >= 4.5;
  const text = hex(stepUntil(mode === 'light' ? { ...fill } : textStart, mode === 'light' ? -1 : 1, textOk));

  return { fill: fillHex, hover, pressed, on, text, focus: focusRingFor(input, mode, text), subtle, subtleHover, border };
}

function deriveSecondary(input: Lch, mode: 'light' | 'dark', adjustments: ThemeAdjustment[], original: string) {
  const base: Lch = { l: input.l, c: Math.min(input.c, 0.14), h: input.h };
  // Secondary is a deep tone used for large brand panels (login, report header): white text at AAA.
  const fill = stepUntil(base, -1, (c) => contrast(WHITE, c) >= 7);
  const fillHex = hex(fill);
  if (Math.abs(fill.l - input.l) > 0.02) {
    adjustments.push({
      role: 'secondary',
      mode,
      from: original,
      to: fillHex,
      reason: 'Darkened so white text on it reaches 7:1 contrast.',
    });
  }
  const subtle = hex(
    mode === 'light'
      ? { l: 0.96, c: Math.min(fill.c * 0.3, 0.03), h: fill.h }
      : { l: 0.25, c: Math.min(fill.c * 0.5, 0.05), h: fill.h },
  );
  return { fill: fillHex, on: WHITE, subtle };
}

function modeTokens(input: BrandInput, mode: 'light' | 'dark', adjustments: ThemeAdjustment[]): Record<string, string> {
  const primary = readColor(input.primary)!;
  const secondary = readColor(input.secondary)!;
  const accent = readColor(input.accent)!;

  const p = deriveFamily(primary, mode, 'primary', adjustments, input.primary);
  const a = deriveFamily(accent, mode, 'accent', adjustments, input.accent);
  const s = deriveSecondary(secondary, mode, adjustments, input.secondary);

  return {
    '--brand': p.fill,
    '--brand-hover': p.hover,
    '--brand-pressed': p.pressed,
    '--on-brand': p.on,
    '--brand-text': p.text,
    '--brand-subtle': p.subtle,
    '--brand-subtle-hover': p.subtleHover,
    '--brand-border': p.border,
    '--focus-ring': p.focus,
    '--accent': a.fill,
    '--accent-hover': a.hover,
    '--on-accent': a.on,
    '--accent-text': a.text,
    '--accent-subtle': a.subtle,
    '--accent-border': a.border,
    '--secondary': s.fill,
    '--on-secondary': s.on,
    '--secondary-subtle': s.subtle,
  };
}

export function sanitizeBrand(input: Partial<BrandInput> | null | undefined): BrandInput {
  const pick = (value: string | undefined, fallback: string) =>
    value && isValidHex(value) ? normalizeHex(value) : fallback;
  return {
    primary: pick(input?.primary, DEFAULT_BRAND.primary),
    secondary: pick(input?.secondary, DEFAULT_BRAND.secondary),
    accent: pick(input?.accent, DEFAULT_BRAND.accent),
  };
}

function normalizeHex(value: string): string {
  const v = value.trim().toLowerCase();
  if (v.length === 4) return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
  return v;
}

export function generateTheme(brand?: Partial<BrandInput> | null): GeneratedTheme {
  const input = sanitizeBrand(brand);
  const adjustments: ThemeAdjustment[] = [];
  const light = modeTokens(input, 'light', adjustments);
  const dark = modeTokens(input, 'dark', []);
  return { light, dark, adjustments, input };
}

function block(selector: string, tokens: Record<string, string>): string {
  const body = Object.entries(tokens)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n');
  return `${selector} {\n${body}\n}`;
}

/** CSS that overrides the platform's default brand tokens. Light by default, dark via attribute or OS preference. */
export function themeToCss(theme: GeneratedTheme): string {
  return [
    block(':root', theme.light),
    block(':root[data-theme="dark"]', theme.dark),
    `@media (prefers-color-scheme: dark) {\n${block(':root:not([data-theme="light"])', theme.dark)
      .split('\n')
      .map((l) => `  ${l}`)
      .join('\n')}\n}`,
  ].join('\n');
}

const STYLE_ID = 'athlete-org-theme';

/** Injects (or replaces) the organization theme stylesheet. Safe to call repeatedly. */
export function applyTheme(theme: GeneratedTheme, doc: Document = document): void {
  let el = doc.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = doc.createElement('style');
    el.id = STYLE_ID;
    doc.head.appendChild(el);
  }
  el.textContent = themeToCss(theme);
}

export function clearTheme(doc: Document = document): void {
  doc.getElementById(STYLE_ID)?.remove();
}
